import SkeletonShippinGroupDetail from "components/Commons/Skeleton/SkeletonShippinGroupDetail"
import { AuthContext } from "context/context/AuthContext"
import { GlobalContext } from "context/context/GlobalContext"
import OrderDetailHOC from "hoc/OrderDetailHOC"
import { OrderDetailType } from "types/OrderType"
import { clients } from "webapp-clients/Clients"
import { Suspense, useContext, useEffect, useState, useMemo } from "react"
import Modal from "components/Commons/Modal/Modal"
import { IconSpinner } from "theme/common/Icon.styled"
import { ButtonStyled } from "theme/common/Button.styled"
import { IconCheck } from "theme/common/Icon.styled"
import { useContractor } from "hooks"
import { IItems } from "interfaces/IItemsOrder"
import ErrorHandler from "components/Commons/Error/ErrorHandler"
import {
  LoadingContainer,
  ShimmerOverlay,
  LoadingContent,
  LoadingSpinner,
  LoadingTitle,
  LoadingMessage,
  ProgressContainer,
  ProgressBar,
  SeriadosTableContainer,
  SeriadosTableTitle,
  NoSeriadosTableContainer,
  NoSeriadosTableTitle,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  SKUCell,
  DescriptionCell,
  QuantityCell,
  ReceivedCell,
  ReceivedInput,
  ReceivedIndicator,
  CompletedIndicator
} from "./NewScreen.styled"

const ContractorReceptionNewScreen = ({ shippinGroupDetail, shippingGroupId, order }: OrderDetailType) => {
  const {
    state: { user },
  } = useContext(AuthContext)

  const { notification, errorHander } = useContext(GlobalContext)

  const { getItemsSeriados, loadingSendData, handleClickSendData } = useContractor()

  const itemsOrder = shippinGroupDetail?.source.items

  // Memoizar los valores de los SKU para evitar recreaciones innecesarias
  const skuValues = useMemo(() => {
    return itemsOrder?.map(item => item.sku) || []
  }, [itemsOrder])

  const channelValues = useMemo(() => {
    return order?.channel || ""
  }, [order])

  // useEffect para imprimir cambios en itemsOrder
  useEffect(() => {
    console.log('📦 Cambio detectado en itemsOrder:', {
      cantidad: itemsOrder?.length || 0,
      items: itemsOrder
    })
  }, [itemsOrder])

  const ContractorScannerlItems =
    user?.mainGroup?.id && clients[user.mainGroup.id as keyof typeof clients]?.ContractorScannerlItems

  const [loadingItemsQuantity, setLoadingItemsQuantity] = useState(false)

  // Arrays para separar items seriados y no seriados
  const [itemsSeriados, setItemsSeriados] = useState<IItems[]>([])
  const [itemsNoSeriados, setItemsNoSeriados] = useState<IItems[]>([])
  
  // Estado para manejar las cantidades recibidas
  const [receivedQuantities, setReceivedQuantities] = useState<{[key: string]: number}>({})
  
  // Estado para manejar errores de validación
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({})
  
  // Estado para controlar la visibilidad del modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  
  // Estado para controlar la visibilidad del modal de error de validación
  const [showValidationErrorModal, setShowValidationErrorModal] = useState(false)

  // Llamar al método getItemsSeriados cuando los SKUs estén disponibles
  useEffect(() => {
    const fetchItemsQuantity = async () => {
      setLoadingItemsQuantity(true)
      try {
        const response = await getItemsSeriados(skuValues, channelValues)

        if(response.data.message.records.length) {
          // Constante con solo los SKUs de los registros
          const skusRecords = response.data.message.records.map((record: IItems) => record.sku)

          // Filtrar itemsOrder basándose en los SKUs de los registros (items seriados)
          const filteredItemsSeriados = itemsOrder?.filter(item => 
            skusRecords.includes(item.sku)
          ).map(item => ({
            ...item,
            custom: {
              ...item.custom,
              seriesReceived: "",
              quantityReceived: 0
            } as any
          })) || []

          // Filtrar itemsOrder que NO están en los SKUs de los registros (items restantes)
          const filteredItemsRestantes = itemsOrder?.filter(item => 
            !skusRecords.includes(item.sku)
          ).map(item => ({
            ...item,
            custom: {
              ...item.custom,
              seriesReceived: "0",
              quantityReceived: item.quantity // Para items no seriados, inicializar con la cantidad enviada
            } as any
          })) || []

          setItemsSeriados(filteredItemsSeriados)
          setItemsNoSeriados(filteredItemsRestantes)

        } else {
          // Si no hay registros seriados, todos los items van a no seriados
          setItemsSeriados([])
          setItemsNoSeriados((itemsOrder || []).map(item => ({
            ...item,
            custom: {
              ...item.custom,
              seriesReceived: "0",
              quantityReceived: item.quantity // Para items no seriados, inicializar con la cantidad enviada
            } as any
          })))
        }


      } catch (error) {
        console.error('Error al obtener cantidad de items por fuente:', error)
      } finally {
        setLoadingItemsQuantity(false)
      }
    }

    if (skuValues.length > 0) {
      setLoadingItemsQuantity(true)
      fetchItemsQuantity()
    }

  },
  //eslint-disable-next-line
  [skuValues, itemsOrder])

  // useEffect para imprimir cambios en las cantidades recibidas
  /*
  useEffect(() => {
    if (Object.keys(receivedQuantities).length > 0) {
      console.log('📊 Cambio detectado en cantidades recibidas:', {
        totalItems: Object.keys(receivedQuantities).length,
        cantidades: receivedQuantities
      })
    }
  }, [receivedQuantities])
  */

  // Función para manejar cambios en las cantidades recibidas
  const handleReceivedQuantityChange = (sku: string, value: string, maxQuantity: number) => {
    const numericValue = parseInt(value) || 0
    
    console.log('📝 Cambio en cantidad recibida:', {
      sku,
      valorAnterior: receivedQuantities[sku] || 0,
      valorNuevo: numericValue,
      valorMaximo: maxQuantity,
      esValido: numericValue <= maxQuantity
    })
    
    // Validar que el valor no sea mayor al máximo permitido
    if (numericValue > maxQuantity) {
      // Mostrar error pero mantener el valor ingresado por el usuario
      setValidationErrors(prev => ({
        ...prev,
        [sku]: `La cantidad debe ser menor o igual a ${maxQuantity}`
      }))
      
      // Mantener el valor ingresado por el usuario
      setReceivedQuantities(prev => ({
        ...prev,
        [sku]: numericValue
      }))
      
      // Actualizar el item no seriado con la nueva cantidad recibida
      setItemsNoSeriados(prev => prev.map(item => 
        item.sku === sku 
          ? {
              ...item,
              custom: {
                ...item.custom,
                quantityReceived: numericValue
              } as any
            }
          : item
      ))
    } else {
      // Limpiar error si existe
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[sku]
        return newErrors
      })
      
      setReceivedQuantities(prev => ({
        ...prev,
        [sku]: numericValue
      }))
      
      // Actualizar el item no seriado con la nueva cantidad recibida
      setItemsNoSeriados(prev => prev.map(item => 
        item.sku === sku 
          ? {
              ...item,
              custom: {
                ...item.custom,
                quantityReceived: numericValue
              } as any
            }
          : item
      ))
    }
  }

  // Función para obtener la cantidad recibida de un item
  const getReceivedQuantity = (sku: string, defaultQuantity: number) => {
    return receivedQuantities[sku] !== undefined ? receivedQuantities[sku] : defaultQuantity
  }

  // Función para verificar si un item seriado está completado
  const isItemCompleted = (item: IItems) => {
    const receivedQuantity = getReceivedQuantity(item.sku, 0)
    return receivedQuantity >= item.quantity
  }

  // Función para validar errores en items no seriados
  const validateNoSeriadosItems = () => {
    // Verificar si hay errores de validación en los items no seriados
    const hasValidationErrors = Object.keys(validationErrors).length > 0
    
    if (hasValidationErrors) {
      // Mostrar modal de error de validación
      setShowValidationErrorModal(true)
      return false
    }
    
    return true
  }

  // Función para mostrar el modal de confirmación
  const handleShowConfirmModal = () => {
    // Validar errores antes de mostrar el modal de confirmación
    if (validateNoSeriadosItems()) {
      setShowConfirmModal(true)
    }
  }

  // Función para cerrar el modal de confirmación
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false)
  }

  // Función para cerrar el modal de error de validación
  const handleCloseValidationErrorModal = () => {
    setShowValidationErrorModal(false)
  }

  // Función para confirmar y ejecutar el envío de datos
  const handleConfirmSendData = () => {
    setShowConfirmModal(false)
    if (shippinGroupDetail && 'source' in shippinGroupDetail) {
      handleClickSendData(order?.channel || "", itemsSeriados, itemsNoSeriados, shippinGroupDetail as any)
    }
  }

  //events
  /**
   * Maneja el procesamiento de productos seriados cuando se escanea o selecciona un SKU
   * Esta función se ejecuta cuando el usuario interactúa con el escáner de productos seriados
   * @param sku - Código SKU del producto a procesar
   * @param serieOriginal - Serie original escaneada o ingresada
   */
  const handleProcessSerializedProduct = (sku: string, serieOriginal: string) => {
    if (sku) {
      console.log('🔍 Procesando producto seriado:', sku)
      
      // Buscar el item en itemsSeriados por SKU
      const itemFound = itemsSeriados.find(item => item.sku === sku)
      
      if (itemFound) {
        // Verificar si el item ya está completado antes de procesar
        const currentReceived = getReceivedQuantity(sku, 0)
        
        if (currentReceived >= itemFound.quantity) {
          // El item ya está completado, mostrar notificación de error
          notification?.dispatch({
            type: "ADD_NOTIFICATION",
            payload: {
              type: "danger",
              title: "SKU Ya Completado",
              message: `No se puede ingresar más series al producto ${itemFound.name} (SKU: ${sku}). Ya se ha recibido la cantidad completa: ${currentReceived}/${itemFound.quantity}`,
              delay: 5000
            }
          })
          
          console.warn('❌ SKU ya completado, no se puede procesar más series:', {
            sku,
            cantidadRecibida: currentReceived,
            cantidadEnviada: itemFound.quantity,
            itemCompleto: itemFound
          })
          
          return // Salir de la función sin procesar la serie
        }
        
        // Aumentar la cantidad recibida en 1
        const newReceivedQuantity = currentReceived + 1
        
        // Verificar que no exceda la cantidad enviada
        if (newReceivedQuantity <= itemFound.quantity) {
          // Actualizar la cantidad recibida
          setReceivedQuantities(prev => ({
            ...prev,
            [sku]: newReceivedQuantity
          }))
          
          // Actualizar el item seriado con las nuevas propiedades
          setItemsSeriados(prev => prev.map(item => {
            if (item.sku === sku) {
              const customAny = item.custom as any
              const currentSeries = customAny?.seriesReceived || ""
              const newSeries = currentSeries ? `${currentSeries};${serieOriginal}` : serieOriginal
              return {
                ...item,
                custom: {
                  ...item.custom,
                  quantityReceived: newReceivedQuantity,
                  seriesReceived: newSeries // Agregar la serie recibida separada por punto y coma
                } as any
              }
            }
            return item
          }))
          
          console.log('✅ Producto seriado procesado exitosamente:', {
            sku,
            serieOriginal,
            cantidadActual: newReceivedQuantity,
            cantidadMaxima: itemFound.quantity,
            itemCompleto: itemFound
          })
          
        } else {
          console.warn('❌ No se puede procesar la serie:', {
            sku,
            cantidadRecibida: newReceivedQuantity,
            cantidadEnviada: itemFound.quantity,
            itemCompleto: itemFound
          })
        }
      } else {
        console.warn('❌ SKU no encontrado en items seriados:', {
          sku,
          itemsSeriadosCompletos: itemsSeriados
        })
      }
    }
  }


  // Componente para renderizar la tabla de items seriados
  const renderSeriadosTable = () => (
    <SeriadosTableContainer>
      <SeriadosTableTitle>
        Productos seriado: Ingrese o escanee las series físicas ({itemsSeriados.length} items)
      </SeriadosTableTitle>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>SKU</TableHeaderCell>
            <TableHeaderCell>Descripción</TableHeaderCell>
            <TableHeaderCell>Enviado</TableHeaderCell>
            <TableHeaderCell>Recibido</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {itemsSeriados.map((item) => {
            const isCompleted = isItemCompleted(item)
            return (
              <TableRow key={item.sku} isCompleted={isCompleted}>
                <SKUCell>{item.sku}</SKUCell>
                <DescriptionCell>
                  {item.name}
                  {isCompleted && <CompletedIndicator />}
                </DescriptionCell>
                <QuantityCell>{item.quantity}</QuantityCell>
                <ReceivedCell>
                  <ReceivedInput
                    type="number"
                    min="0"
                    max={item.quantity}
                    value={getReceivedQuantity(item.sku, 0)}
                    readOnly
                    hasError={false}
                  />
                  <ReceivedIndicator isCompleted={isCompleted} />
                </ReceivedCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </SeriadosTableContainer>
  )

  // Componente para renderizar la tabla de items no seriados
  const renderNoSeriadosTable = () => (
    <NoSeriadosTableContainer>
      <NoSeriadosTableTitle>
        Productos no seriado: Ingrese la cantidad física ({itemsNoSeriados.length} items)
      </NoSeriadosTableTitle>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>SKU</TableHeaderCell>
            <TableHeaderCell>Descripción</TableHeaderCell>
            <TableHeaderCell>Enviado</TableHeaderCell>
            <TableHeaderCell>Recibido</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {itemsNoSeriados.map((item) => (
            <TableRow key={item.sku}>
              <SKUCell>{item.sku}</SKUCell>
              <DescriptionCell>{item.name}</DescriptionCell>
              <QuantityCell>{item.quantity}</QuantityCell>
              <ReceivedCell>
                <ReceivedInput
                  type="number"
                  min="0"
                  max={item.quantity}
                  value={getReceivedQuantity(item.sku, item.quantity)}
                  onChange={(e) => handleReceivedQuantityChange(item.sku, e.target.value, item.quantity)}
                  hasError={!!validationErrors[item.sku]}
                />
                <ReceivedIndicator />
                {validationErrors[item.sku] && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {validationErrors[item.sku]}
                  </div>
                )}
              </ReceivedCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </NoSeriadosTableContainer>
  )
  
  if (loadingSendData) {
    return (
      <>
        <LoadingContainer>
          {/* Animación de fondo */}
          <ShimmerOverlay />
          
          {/* Contenido del loading */}
          <LoadingContent>
            {/* Spinner animado */}
            <LoadingSpinner />
            
            {/* Mensaje principal */}
            <LoadingTitle>
              🔄 Procesando orden para transferencia de productos
            </LoadingTitle>
            
            {/* Mensaje secundario */}
            <LoadingMessage>
            Por favor, espere un momento mientras validamos los procesos de transferencia de la orden...
            </LoadingMessage>
            
            {/* Indicador de progreso animado */}
            <ProgressContainer>
              <ProgressBar />
            </ProgressContainer>
          </LoadingContent>
        </LoadingContainer>
      </>
    )  
  }

  return (
    <>
      {errorHander?.state?.hasError ? (
        <ErrorHandler 
          code={errorHander.state.code} 
          message={errorHander.state.message || "Ha ocurrido un error inesperado"} 
        />
      ) : itemsOrder ? (
        <>
          {/* Mostrar información de cantidad de items por fuente */}
          {loadingItemsQuantity && (
            <LoadingContainer>
              {/* Animación de fondo */}
              <ShimmerOverlay />
              
              {/* Contenido del loading */}
              <LoadingContent>
                {/* Spinner animado */}
                <LoadingSpinner />
                
                {/* Mensaje principal */}
                <LoadingTitle>
                  🔄 Procesando información de productos
                </LoadingTitle>
                
                {/* Mensaje secundario */}
                <LoadingMessage>
                  Verificando disponibilidad y categorización de productos...
                </LoadingMessage>
                
                {/* Indicador de progreso animado */}
                <ProgressContainer>
                  <ProgressBar />
                </ProgressContainer>
              </LoadingContent>
            </LoadingContainer>
          )}

          {/* Mostrar tabla de items no seriados */}
          {itemsNoSeriados.length > 0 && !loadingItemsQuantity && renderNoSeriadosTable()}

          {/* Mostrar tabla de items seriados */}
          {itemsSeriados.length > 0 && !loadingItemsQuantity && renderSeriadosTable()}

          {/* Componente de escaneo para items seriados */}
          {itemsSeriados.length > 0 && !loadingItemsQuantity && ContractorScannerlItems && (
            <Suspense fallback={<IconSpinner variant="big" className="content-center" />}>
              <ContractorScannerlItems
                onClicProcessProduct={handleProcessSerializedProduct}
                source={shippinGroupDetail?.source}
                type="reception"
              />
            </Suspense>
          )}
          
          {/* Mostrar botón solo si existen items seriados o no seriados */}
          {(itemsSeriados.length > 0 || itemsNoSeriados.length > 0) && !loadingItemsQuantity && (
            <div style={{ marginTop: '3rem' }}>
              <ButtonStyled
                variant="primary"
                onClick={handleShowConfirmModal}
                loadingButton={loadingSendData}
                disabled={loadingSendData}
                fullSize
              >
                <IconCheck />
                <span>Recibir</span>
              </ButtonStyled>
            </div>
          )}

          {/* Modal de confirmación */}
          {showConfirmModal && (
            <Modal defaultOpened={showConfirmModal}>
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                padding: '2rem', 
                maxWidth: '600px', 
                width: '90vw',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ marginBottom: '1rem', fontSize: '16px', lineHeight: '1.5' }}>
                    <strong>Por favor, revise cuidadosamente la información ingresada antes de confirmar:</strong>
                  </p>
                  
                  <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', padding: '1rem', borderRadius: '8px' }}>
                    <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                      <strong>⚠️ Importante:</strong> Una vez confirmada la recepción, no podrá modificar los datos ingresados. 
                      Asegúrese de que las cantidades y series sean correctas.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <ButtonStyled
                    variant="secondary"
                    onClick={handleCloseConfirmModal}
                  >
                    Cancelar
                  </ButtonStyled>
                  <ButtonStyled
                    variant="primary"
                    onClick={handleConfirmSendData}
                    loadingButton={loadingSendData}
                    disabled={loadingSendData}
                  >
                    <IconCheck />
                    Confirmar
                  </ButtonStyled>
                </div>
              </div>
            </Modal>
          )}

          {/* Modal de error de validación */}
          {showValidationErrorModal && (
            <Modal defaultOpened={showValidationErrorModal}>
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                padding: '2rem', 
                maxWidth: '500px', 
                width: '90vw',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                
                <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '48px', 
                    marginBottom: '1rem',
                    color: '#dc3545'
                  }}>
                    ⚠️
                  </div>
                  
                  <h3 style={{ 
                    marginBottom: '1rem', 
                    fontSize: '18px', 
                    color: '#dc3545',
                    fontWeight: 'bold'
                  }}>
                    Error de Validación
                  </h3>
                  
                  <p style={{ 
                    marginBottom: '1rem', 
                    fontSize: '16px', 
                    lineHeight: '1.5',
                    color: '#6c757d'
                  }}>
                    Existe un error en los productos no seriados que debe verificar antes de continuar.
                  </p>
                  
                  <div style={{ 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, color: '#721c24', fontSize: '14px' }}>
                      <strong>Por favor:</strong> Revise las cantidades ingresadas en los productos no seriados y corrija los errores antes de proceder con la recepción.
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <ButtonStyled
                    variant="primary"
                    onClick={handleCloseValidationErrorModal}
                  >
                    Entendido
                  </ButtonStyled>
                </div>
              </div>
            </Modal>
          )}
        </>
      ) : (
        <SkeletonShippinGroupDetail />
      )}
    </>
  )
}

export default OrderDetailHOC(ContractorReceptionNewScreen)
